import {observer} from 'mobx-react-lite'
import React, {ReactNode, useEffect, useMemo} from 'react'
import {Lang} from 'yougile-lang'
import {Util} from 'yougile-util'
import {UiSlot} from '@api/components'
import {Tracking} from '@common/tracking'
import {TrackingEvent} from '@common/tracking-events'
import {useMainMenuStore} from '@components/main-menu/main-menu-store'
import {PageHeader} from '@components/page/page-header'
import {ByTime} from '@components/statistics/by-time/by-time'
import {ByUser} from '@components/statistics/by-user/by-user'
import {StatsAggregatedReport} from '@components/statistics/stats-reports/stats-aggregated-report'
import {StatsReports} from '@components/statistics/stats-reports/stats-reports'
import {PageService} from '@extensions/page-service'

interface Props {
  subpage: StatisticsSubpage
}

const LegacyStyleRoutes = ['by-user', 'tables', 'report', 'by-time']
export type StatisticsSubpageLegacy =
  | 'by-user'
  | 'tables'
  | 'report'
  | 'by-time'
export type StatisticsSubpage = StatisticsSubpageLegacy | string

const clickEvents = {
  'by-user': TrackingEvent.byUserClick,
  'by-time': TrackingEvent.byTimeClick,
  tables: TrackingEvent.reportsClick,
  report: TrackingEvent.reportClick
}

const goTo = (subpage: StatisticsSubpage) => () => {
  if (LegacyStyleRoutes.includes(subpage)) {
    void Tracking.send(clickEvents[subpage as StatisticsSubpageLegacy])
  }
  PageService.goToStatistics(subpage)
}

const SubPage = ({subpage}: {subpage: StatisticsSubpage}) => {
  if (subpage === 'tables') {
    return <StatsReports />
  }
  if (subpage === 'by-user') {
    return <ByUser />
  }
  if (subpage === 'by-time') {
    return <ByTime />
  }
  if (!subpage || subpage === 'report') {
    return <StatsAggregatedReport />
  }

  return null
}

const bem = Util.bemFrom('statistics')

const CustomNavItem = ({
  title,
  icon,
  selected,
  onClick,
  to
}: {
  title: string
  icon: ReactNode
  selected: boolean
  onClick?: () => void
  to?: string
}) => {
  const toHandler = useMemo(() => (to ? goTo(to) : undefined), [to, onClick])
  return (
    <div
      className={bem('nav-item', {
        current: selected,
        ['custom']: true
      })}
      onClick={() => {
        onClick?.()
        toHandler?.()
      }}
    >
      <div className={bem('nav-item-custom-icon')}>{icon}</div>
      <div>{title}</div>
    </div>
  )
}

const NavItem = ({
  title,
  name,
  current,
  onClick
}: {
  title: string
  name: string
  current: boolean
  onClick?: () => void
  to?: string
}) => {
  return (
    <div
      className={bem('nav-item', {
        current: current,
        ['page-' + name]: true
      })}
      onClick={onClick}
    >
      {title}
    </div>
  )
}

export const StatisticsBase = observer((props: Props) => {
  const [menuState] = useMainMenuStore()
  const {subpage} = props
  const nav = (p: StatisticsSubpage, title: string) => (
    <NavItem
      title={title}
      name={p}
      current={(subpage || 'report') === p}
      onClick={goTo(p)}
    />
  )

  useEffect(() => {
    if (!menuState.canSeeStatsPage) {
      PageService.goTo('projects')
    }
  }, [menuState.canSeeStatsPage])

  return (
    <div className={bem()}>
      <PageHeader>{Lang.msg.statistics}</PageHeader>

      <div className={bem('content')}>
        <div className={bem('nav')}>
          {nav('report', Lang.msg.report.report)}
          {nav('tables', Lang.msg.report.tables)}
          {nav('by-user', Lang.msg.byUserPageTitle)}
          {nav('by-time', Lang.msg.byTimeInColumns)}
          <UiSlot
            name="statistics-nav-after"
            data={{
              NavItem: CustomNavItem,
              page: subpage
            }}
          />
        </div>

        <div className={bem('container')}>
          <SubPage subpage={subpage} />
          <UiSlot
            name="statistics-page-content"
            data={{
              page: subpage
            }}
          />
        </div>
      </div>
    </div>
  )
})
